import { useMemo, useState } from "react";
import { isStepTestPath } from "../../shared/stepConfig";

// A React-key signature of the files a step serves at the current head commit — head_commit_id is stable across an LLM turn (files mutate in place on the working commit), so we key on the file blobs, not the commit id.
type FileRow = { path: string; blobHash: string };

export function stepContentKey(
  stepDir: string,
  files: readonly FileRow[] | undefined
): string {
  if (!files) return "";
  const prefix = `${stepDir}/`;
  return files
    .filter((f) => f.path.startsWith(prefix) && !isStepTestPath(f.path))
    .map((f) => f.blobHash)
    .sort()
    .join(":");
}

// The signature as of the last moment the agent wasn't writing. A turn rewrites
// a step's files several times, and reloading the page at each half-written
// state shows work in progress the user didn't ask to see and throws away
// whatever they were doing in the page.
export function useSettledStepContentKey(
  stepDir: string,
  files: readonly FileRow[] | undefined,
  isResponding: boolean
): string {
  const key = useMemo(() => stepContentKey(stepDir, files), [stepDir, files]);
  // State adjusted during render rather than a ref write, so a render React
  // discards can't publish a mid-turn key.
  const [settled, setSettled] = useState(key);
  if (!isResponding && settled !== key) setSettled(key);
  return isResponding ? settled : key;
}
